import {
	Alert,
	AlertDescription,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Progress,
	Textarea,
} from "@ancatag/at-design";
import { ArrowLeft, ArrowRight, Loader2, Radio, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import {
	type CreateModelConfigData,
	modelConfigsService,
	type ProviderModel,
} from "../../services/models";
import type {
	GenerationType,
	ProviderTemplate,
} from "../../services/provider-templates";
import { Step1GenerationType } from "./steps/Step1GenerationType";
import { Step2Provider } from "./steps/Step2Provider";
import { Step3BYOPConfig } from "./steps/Step3BYOPConfig";
import { Step3NovaHosted } from "./steps/Step3NovaHosted";

export interface ModelWizardData {
	generationType: GenerationType | null;
	provider: ProviderTemplate | null;
	isHosted: boolean;
	credentials: {
		baseUrl?: string;
		apiKey?: string;
		orgId?: string;
	} | null;
	selectedModel: ProviderModel | null;
	availableModels: ProviderModel[];
	modelName: string;
	description: string;
	modelIdentifier: string;
	modelDisplayName: string;
}

const TOTAL_STEPS = 5;

export function NewModel() {
	const [, setLocation] = useLocation();
	const [currentStep, setCurrentStep] = useState(1);
	const [saving, setSaving] = useState(false);
	const [loadingModels, setLoadingModels] = useState(false);

	const [wizardData, setWizardData] = useState<ModelWizardData>({
		generationType: null,
		provider: null,
		isHosted: false,
		credentials: null,
		selectedModel: null,
		availableModels: [],
		modelName: "",
		description: "",
		modelIdentifier: "",
		modelDisplayName: "",
	});

	const updateWizardData = (updates: Partial<ModelWizardData>) => {
		setWizardData((prev) => ({ ...prev, ...updates }));
	};

	const canProceed = () => {
		switch (currentStep) {
			case 1:
				return wizardData.generationType !== null;
			case 2:
				return wizardData.provider !== null;
			case 3:
				if (wizardData.isHosted) {
					// For Nova-hosted, model must be selected
					return (
						wizardData.modelIdentifier !== undefined &&
						wizardData.modelIdentifier !== ""
					);
				}
				// For self-hosted, credentials must be set and connection tested
				return wizardData.credentials?.baseUrl !== undefined;
			case 4:
				return wizardData.selectedModel !== null;
			case 5:
				return (
					wizardData.modelName.trim().length > 0 &&
					wizardData.modelIdentifier.trim().length > 0
				);
			default:
				return false;
		}
	};

	const handleNext = async () => {
		if (currentStep === 3) {
			if (wizardData.isHosted) {
				// For Nova-hosted, model selection happens in Step3NovaHosted
				// Skip to step 5 (configuration) if model is selected
				if (wizardData.modelIdentifier) {
					setCurrentStep(5);
				}
			} else {
				// For self-hosted, test connection and fetch models
				// Step3BYOPConfig handles its own test connection
				// We proceed to step 4 (model selection) if credentials are set
				if (wizardData.credentials?.baseUrl) {
					setCurrentStep(4);
				}
			}
		} else if (canProceed() && currentStep < TOTAL_STEPS) {
			setCurrentStep((prev) => prev + 1);
		}
	};

	const handleBack = () => {
		if (currentStep > 1) {
			setCurrentStep((prev) => prev - 1);
		}
	};

	const handleFetchModels = async () => {
		if (!wizardData.credentials?.baseUrl) {
			toast.error("Please provide a base URL");
			return;
		}

		setLoadingModels(true);
		try {
			if (!wizardData.provider?.identifier) {
				toast.error("Please select a provider");
				return;
			}

			// Create temp model to fetch models
			const tempModel = await modelConfigsService.create({
				name: "Temp Fetch Model",
				generationType: wizardData.generationType!,
				providerType: wizardData.provider.identifier,
				modelIdentifier: "test",
				isHosted: false,
				credentials: wizardData.credentials,
			});

			const modelId = tempModel.data?.id || (tempModel as any).id;
			if (!modelId) {
				throw new Error("Failed to create temporary model");
			}

			const models = await modelConfigsService.listProviderModels(modelId);
			updateWizardData({ availableModels: models });

			// Delete temp model
			await modelConfigsService.delete(modelId);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to fetch models",
			);
		} finally {
			setLoadingModels(false);
		}
	};

	const handleFinish = async () => {
		if (!canProceed()) {
			toast.error("Please complete all required fields");
			return;
		}

		if (!wizardData.provider?.identifier) {
			toast.error("Please select a provider");
			return;
		}

		setSaving(true);
		try {
			const modelData: CreateModelConfigData = {
				name: wizardData.modelName,
				description: wizardData.description || undefined,
				generationType: wizardData.generationType!,
				providerType: wizardData.provider.identifier,
				modelIdentifier: wizardData.modelIdentifier,
				modelDisplayName: wizardData.modelDisplayName || undefined,
				isHosted: wizardData.isHosted,
				credentials: wizardData.isHosted
					? undefined
					: wizardData.credentials || undefined,
				isActive: true,
			};

			await modelConfigsService.create(modelData);
			toast.success("Model connection created successfully!");
			setLocation("/models/manage");
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to create model",
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="min-h-screen bg-background p-4 md:p-8">
			<Card className="max-w-4xl mx-auto">
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle className="text-2xl">
								Create New Model Connection
							</CardTitle>
							<CardDescription>
								Step {currentStep} of {TOTAL_STEPS}
							</CardDescription>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setLocation("/models/manage")}
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					<div className="mb-6">
						<Progress
							value={(currentStep / TOTAL_STEPS) * 100}
							className="h-2"
						/>
					</div>

					<div className="min-h-[300px] md:min-h-[400px]">
						{currentStep === 1 && (
							<Step1GenerationType
								selectedType={wizardData.generationType}
								onSelect={(type) => updateWizardData({ generationType: type })}
							/>
						)}
						{currentStep === 2 && (
							<Step2Provider
								generationType={wizardData.generationType!}
								selectedProvider={wizardData.provider}
								onSelect={(provider, isHosted) =>
									updateWizardData({ provider, isHosted })
								}
							/>
						)}
						{currentStep === 3 &&
							wizardData.provider &&
							(wizardData.isHosted ? (
								<Step3NovaHosted
									provider={wizardData.provider}
									onModelSelect={(modelId) => {
										updateWizardData({
											modelIdentifier: modelId,
											selectedModel:
												wizardData.availableModels?.find(
													(m) => m.id === modelId,
												) || null,
										});
									}}
									selectedModelId={wizardData.modelIdentifier}
								/>
							) : (
								<Step3BYOPConfig
									provider={wizardData.provider}
									isHosted={false}
									credentials={wizardData.credentials}
									generationType={wizardData.generationType!}
									onCredentialsChange={(creds) => {
										updateWizardData({ credentials: creds });
									}}
									onDraftConfigCreated={() => {
										// Draft config creation not needed for ModelConfig wizard
									}}
								/>
							))}
						{currentStep === 4 && (
							<div className="space-y-6">
								{wizardData.isHosted ? (
									<Alert>
										<AlertDescription>
											Model selection completed in previous step. Proceed to
											configuration.
										</AlertDescription>
									</Alert>
								) : (
									<div>
										<h3 className="text-lg font-semibold mb-4">Select Model</h3>
										{wizardData.availableModels.length === 0 ? (
											<div className="space-y-4">
												<Alert>
													<AlertDescription>
														No models found. Please test the connection first or
														fetch models manually.
													</AlertDescription>
												</Alert>
												<Button
													onClick={handleFetchModels}
													disabled={loadingModels}
												>
													{loadingModels ? (
														<>
															<Loader2 className="h-4 w-4 mr-2 animate-spin" />
															Fetching...
														</>
													) : (
														"Fetch Models"
													)}
												</Button>
											</div>
										) : (
											<div className="space-y-2">
												{wizardData.availableModels.map((model) => (
													<div
														key={model.id}
														className={`p-4 border rounded-lg cursor-pointer transition-colors ${
															wizardData.selectedModel?.id === model.id
																? "border-primary bg-primary/10"
																: "border-border hover:border-primary/50"
														}`}
														onClick={() => {
															updateWizardData({
																selectedModel: model,
																modelIdentifier: model.id,
																modelDisplayName: model.name,
															});
														}}
													>
														<div className="flex items-center gap-3">
															<Radio
																className={`h-4 w-4 ${
																	wizardData.selectedModel?.id === model.id
																		? "text-primary"
																		: "text-muted-foreground"
																}`}
															/>
															<div>
																<p className="font-semibold">{model.name}</p>
																<p className="text-sm text-muted-foreground font-mono">
																	{model.id}
																</p>
															</div>
														</div>
													</div>
												))}
											</div>
										)}
									</div>
								)}
							</div>
						)}
						{currentStep === 5 && (
							<div className="space-y-6">
								<div>
									<h3 className="text-lg font-semibold mb-4">
										Name Your Model
									</h3>
									<div className="space-y-4">
										<div>
											<Label htmlFor="modelName">Model Name *</Label>
											<Input
												id="modelName"
												placeholder="My Ollama Llama2"
												value={wizardData.modelName}
												onChange={(e) =>
													updateWizardData({ modelName: e.target.value })
												}
											/>
										</div>
										<div>
											<Label htmlFor="description">Description</Label>
											<Textarea
												id="description"
												placeholder="Optional description..."
												value={wizardData.description}
												onChange={(e) =>
													updateWizardData({ description: e.target.value })
												}
												rows={3}
											/>
										</div>
										{wizardData.selectedModel && (
											<div className="p-4 border rounded-lg bg-muted/50">
												<p className="text-sm text-muted-foreground mb-2">
													Selected Model:
												</p>
												<p className="font-semibold">
													{wizardData.modelDisplayName}
												</p>
												<p className="text-sm text-muted-foreground font-mono">
													{wizardData.modelIdentifier}
												</p>
											</div>
										)}
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="flex justify-between mt-8">
						<Button
							variant="outline"
							onClick={handleBack}
							disabled={currentStep === 1}
						>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back
						</Button>
						{currentStep < TOTAL_STEPS ? (
							<Button onClick={handleNext} disabled={!canProceed()}>
								Next
								<ArrowRight className="h-4 w-4 ml-2" />
							</Button>
						) : (
							<Button onClick={handleFinish} disabled={!canProceed() || saving}>
								{saving ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Creating...
									</>
								) : (
									"Create Model Connection"
								)}
							</Button>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
